import { css } from '@emotion/css';

import { InputContext } from './InputContext';
import * as Hint from './internal/Hint';
import * as Input from './internal/Input';
import * as InputError from './internal/InputError';
import * as Label from './internal/Label';
import * as Underline from './internal/Underline';
import React, { useEffect, useRef, useState } from 'react';
import { ClickableFactory } from '../../ui/Clickable';

export interface Props {
    errorText?: React.ReactNode | null;
    labelText?: React.ReactNode;
    hintText?: React.ReactNode;
    maxChars?: number;
    context?: InputContext;
    autoCapitalize?: string;
    autoCorrect?: string;
    name?: string;
    autocomplete?: string;
    onChange?: ((value: string) => void) | undefined;
    onEnter?: () => void;
    onBlur?: () => void;
    onFocus?: () => void;
    inputType?: string;
    value: string | null;
    key?: string;
    tabIndex?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    inputStyle?: any;
    useAutoCorrectInput?: boolean;
    readOnly?: boolean | undefined;
    autoFocus?: boolean;
    required?: boolean;
}

export function hintIsVisible(props: Props, focused: boolean): boolean {
    if (!props.hintText) {
        return false;
    }
    if (props.value) {
        return false;
    }
    if (props.labelText && !focused) {
        return false;
    }
    return true;
}

export function getUnderlineStatus(
    focused: boolean,
    errorText: React.ReactNode | null,
): Underline.UnderlineStatus {
    if (errorText) {
        return Underline.UnderlineStatus.error;
    }

    return focused
        ? Underline.UnderlineStatus.focus
        : Underline.UnderlineStatus.blur;
}

export function TextField(props: Props) {
    const {
        context = InputContext.form,
        labelText = '',
        hintText = '',
        errorText = '',
        maxChars = 50,
        inputType = 'text',
        value = null,
        onChange,
        onBlur,
        onFocus,
        onEnter,
        autoCapitalize,
        autoCorrect,
        name,
        autocomplete,
        autoFocus,
        readOnly,
        required,
        tabIndex,
        inputStyle,
        useAutoCorrectInput,
    } = props;

    const [focused, setFocused] = useState<boolean>(false);
    const inputRef = useRef<Input.InputHandle>(null);

    useEffect(() => {
        if (autoFocus === true) {
            inputRef.current?.focus();
        }
    }, [autoFocus]);

    return ClickableFactory(
        {
            element: 'div',
            className: css({
                marginTop: '8px',
                display: 'block',
                position: 'relative',
            }),
            onClick: (): void => {
                if (!focused) {
                    inputRef.current?.focus();
                    setFocused(true);
                }
            },
        },
        Label.Factory({
            text: labelText || '',
            context: context || InputContext.form,
            focused: focused,
            elevated: focused || !!value,
        }),
        Hint.Factory({
            visible: hintIsVisible(props, focused),
            context: context || InputContext.form,
            text: hintText || '',
        }),
        Input.Factory({
            ref: inputRef,
            inputType: inputType || 'text',
            context: context || InputContext.form,
            maxChars: maxChars || 40,
            autoCapitalize: autoCapitalize,
            autoCorrect: autoCorrect,
            name: name,
            autocomplete: autocomplete,
            autoFocus: autoFocus,
            disabled: readOnly,
            required: required,
            onBlur: () => {
                setFocused(false);
                if (onBlur) {
                    onBlur();
                }
            },
            onFocus: () => {
                setFocused(true);
                if (onFocus) {
                    onFocus();
                }
            },
            onChange:
                onChange ||
                ((): null => {
                    return null;
                }),
            onKeyDown: (e): void => {
                if (onEnter && e?.key === 'Enter') {
                    onEnter();
                }
            },
            value: value || '',
            tabIndex: tabIndex,
            style: inputStyle,
            useAutoCorrectInput: useAutoCorrectInput,
        }),
        !readOnly &&
            Underline.Factory({
                status: getUnderlineStatus(focused, errorText || null),
                context: context || InputContext.form,
            }),
        InputError.Factory({
            context: context || InputContext.form,
            text: errorText || '',
        }),
    );
}

export const Factory = (
    props: Props,
    ...children: React.ReactNode[]
): React.ReactElement => React.createElement(TextField, props, ...children);
